/**
 * 拖动替换图片插件的来源判定与视口坐标工具
 *
 * 供 drop-img-plugin 的全局移动监听调用，依赖事件节点属性、当前激活图层与框架基础操作事件，
 * 用于兼容激活操作框、旧版图片节点以及未激活图层直接拖动三种事件来源。
 */

import { BaseLayerViewType, LayerType, type ILayerProps } from '@/version-types';

type DragEventTarget = Pick<HTMLElement, 'getAttribute'>;

/**
 * 判断事件目标是否正在拖动当前激活的图片图层
 *
 * @param target - 拖动开始时命中的 DOM 节点
 * @param activeLayer - 当前激活图层
 * @param moveLayerId - 框架基础操作事件记录的移动图层 ID
 * @returns 是否应进入图片拖动替换流程
 */
export const checkIsImageDragSource = (
    target: DragEventTarget,
    activeLayer: ILayerProps | null,
    moveLayerId?: string | null,
) => {
    if (!activeLayer || activeLayer.layerType !== LayerType.IMAGE_LAYER) return false;

    const operateType = target.getAttribute('data-operate-type');
    const layerType = target.getAttribute('data-layer-type');
    const layerId = target.getAttribute('data-layer-id');

    return (
        operateType === BaseLayerViewType.MOVE ||
        layerType === LayerType.IMAGE_LAYER ||
        layerId === activeLayer.id ||
        moveLayerId === activeLayer.id
    );
};

/**
 * 计算图层在编辑器视口中的中心点
 *
 * 长画布图层的 top 是页内坐标，需要先叠加所属 Frame 的纵向偏移，再应用画布缩放和平移。
 *
 * @param layer - 待计算的图层
 * @param viewport - 当前画布缩放和平移信息
 * @param frameOffsetTop - 图层所属 Frame 在长画布中的纵向偏移
 * @returns 图层中心点的视口坐标
 */
export const getLayerViewportCenter = (
    layer: ILayerProps,
    viewport: { zoom: number; translateX: number; translateY: number },
    frameOffsetTop = 0,
) => ({
    x: (layer.left + layer.width / 2) * viewport.zoom + viewport.translateX,
    y: (layer.top + frameOffsetTop + layer.height / 2) * viewport.zoom + viewport.translateY,
});
